import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

// البيانات الصحيحة المطلوبة
const CORRECT_STORE_DATA = {
  storeName: 'اليكترونيات السواح',
  storeEmail: 'info@alsawah-electronics.com',
  storePhone: '0123456789',
  storeDescription: 'متخصصون في بيع وصيانة جميع أنواع الأجهزة الإلكترونية والكهربائية بأفضل الأسعار وأعلى جودة.'
};

// البيانات الافتراضية التي يجب منعها
const FORBIDDEN_DEFAULT_VALUES = [
  'المتجر الإلكتروني',
  'مركز صيانة التقنية',
  'info@store.com',
  'info@example.com',
  '+20 123 456 7890',
  'القاهرة، مصر',
  'القاهرة، جمهورية مصر العربية',
  '01012345678'
];

// دالة للتحقق من وجود قيم افتراضية
export const detectDefaultValues = (data: any): string[] => {
  const foundDefaults: string[] = [];
  
  const checkValue = (value: any, path: string) => {
    if (typeof value === 'string') {
      FORBIDDEN_DEFAULT_VALUES.forEach(forbidden => {
        if (value.includes(forbidden)) {
          foundDefaults.push(`${path}: ${value} (contains: ${forbidden})`);
        }
      });
    } else if (typeof value === 'object' && value !== null) {
      Object.keys(value).forEach(key => {
        checkValue(value[key], `${path}.${key}`);
      });
    }
  };
  
  checkValue(data, 'root');
  return foundDefaults;
};

// دالة لإصلاح البيانات تلقائياً
export const autoFixData = async (): Promise<boolean> => {
  try {
    console.log('🔧 بدء الإصلاح التلقائي للبيانات...');
    
    // إصلاح إعدادات المتجر
    const storeRef = doc(db, 'settings', 'store');
    const storeDoc = await getDoc(storeRef);
    
    if (storeDoc.exists()) {
      const currentData = storeDoc.data();
      const defaults = detectDefaultValues(currentData);
      
      if (defaults.length > 0) {
        console.log('⚠️ تم اكتشاف قيم افتراضية:', defaults);
        
        // إصلاح البيانات
        const fixedData = {
          ...currentData,
          general: {
            ...currentData.general,
            ...CORRECT_STORE_DATA,
            socialLinks: {
              facebook: 'https://facebook.com/alsawah-electronics',
              twitter: 'https://twitter.com/alsawah-electronics',
              instagram: 'https://instagram.com/alsawah-electronics',
              youtube: 'https://youtube.com/alsawah-electronics',
              whatsapp: 'https://wa.me/0123456789'
            },
            workingHours: {
              saturday: '9:00 صباحًا - 9:00 مساءً',
              sunday: '9:00 صباحًا - 9:00 مساءً',
              monday: '9:00 صباحًا - 9:00 مساءً',
              tuesday: '9:00 صباحًا - 9:00 مساءً',
              wednesday: '9:00 صباحًا - 9:00 مساءً',
              thursday: '9:00 صباحًا - 9:00 مساءً',
              friday: '2:00 مساءً - 9:00 مساءً'
            },
            isStoreEnabled: true
          },
          lastUpdated: new Date(),
          autoFixed: true,
          autoFixedAt: new Date()
        };
        
        await setDoc(storeRef, fixedData, { merge: false });
        console.log('✅ تم إصلاح البيانات تلقائياً');
        return true;
      }
    }
    
    return false;
  } catch (error) {
    console.error('❌ خطأ في الإصلاح التلقائي:', error);
    return false;
  }
};

// دالة مراقبة البيانات في الوقت الفعلي
export const startDataMonitoring = (): (() => void) => {
  console.log('👁️ بدء مراقبة البيانات في الوقت الفعلي...');
  
  const storeRef = doc(db, 'settings', 'store');
  
  const unsubscribe = onSnapshot(storeRef, async (doc) => {
    if (doc.exists()) {
      const data = doc.data();
      const defaults = detectDefaultValues(data);
      
      if (defaults.length > 0) {
        console.warn('🚨 تم اكتشاف قيم افتراضية في البيانات!');
        console.warn('القيم المكتشفة:', defaults);
        
        // إصلاح تلقائي
        const fixed = await autoFixData();
        if (fixed) {
          console.log('✅ تم الإصلاح التلقائي');
          
          // إعادة تحميل الصفحة لتطبيق التغييرات
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      } else {
        console.log('✅ البيانات سليمة - لا توجد قيم افتراضية');
      }
    }
  });
  
  return unsubscribe;
};

// دالة للتحقق الدوري
export const startPeriodicCheck = (): NodeJS.Timeout => {
  console.log('⏰ بدء التحقق الدوري كل 30 ثانية...');
  
  return setInterval(async () => {
    try {
      const storeRef = doc(db, 'settings', 'store');
      const storeDoc = await getDoc(storeRef);
      
      if (storeDoc.exists()) {
        const data = storeDoc.data();
        const defaults = detectDefaultValues(data);
        
        if (defaults.length > 0) {
          console.warn('🔄 التحقق الدوري: تم اكتشاف قيم افتراضية');
          await autoFixData();
        }
      }
    } catch (error) {
      console.error('❌ خطأ في التحقق الدوري:', error);
    }
  }, 30000); // كل 30 ثانية
};

// دالة لحماية localStorage
export const protectLocalStorage = (): void => {
  const originalSetItem = localStorage.setItem;
  
  localStorage.setItem = function(key: string, value: string) {
    // التحقق من البيانات قبل الحفظ
    if (key.includes('Settings') || key.includes('store')) {
      try {
        const data = JSON.parse(value);
        const defaults = detectDefaultValues(data);
        
        if (defaults.length > 0) {
          console.warn('🚫 منع حفظ بيانات افتراضية في localStorage:', defaults);
          return; // منع الحفظ
        }
      } catch (e) {
        // تجاهل أخطاء JSON
      }
    }
    
    originalSetItem.call(this, key, value);
  };
};

// دالة شاملة لبدء جميع أنظمة الحماية
export const startFullProtection = (): (() => void) => {
  console.log('🛡️ بدء نظام الحماية الشامل...');
  
  // حماية localStorage
  protectLocalStorage();
  
  // بدء المراقبة في الوقت الفعلي
  const unsubscribeMonitoring = startDataMonitoring();
  
  // بدء التحقق الدوري
  const periodicInterval = startPeriodicCheck();
  
  // إصلاح فوري
  autoFixData();
  
  // دالة لإيقاف جميع أنظمة الحماية
  return () => {
    console.log('🛑 إيقاف نظام الحماية...');
    unsubscribeMonitoring();
    clearInterval(periodicInterval);
  };
};

// إتاحة الدوال من وحدة التحكم
(window as any).detectDefaultValues = detectDefaultValues;
(window as any).autoFixData = autoFixData;
(window as any).startFullProtection = startFullProtection;

export default {
  detectDefaultValues,
  autoFixData,
  startDataMonitoring,
  startPeriodicCheck,
  protectLocalStorage,
  startFullProtection
};
